import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Component to handle zoom events and marker updates
function ZoomHandler({ onZoomChange, markers, selectedCountry, onLocationClick, isListSelection }) {
    const [currentZoom, setCurrentZoom] = useState(9);
    const map = useMap();

    useMapEvents({
        zoom: () => {
            const zoom = map.getZoom();
            setCurrentZoom(zoom);
            onZoomChange(zoom);
        }
    });

    // Sync initial zoom with map
    useEffect(() => {
        const zoom = map.getZoom();
        setCurrentZoom(zoom);
    }, [map]);

    // Calculate marker size based on zoom level and connection count
    const getMarkerRadius = (marker, maxCount) => {
        const { count, location } = marker;
        const baseSize = Math.min(currentZoom * 2, 20); // Base size increases with zoom
        
        // If it's a list selection (click from the city list), show only the selected city enlarged
        if (isListSelection && selectedCountry) {
            if (location === selectedCountry) {
                return Math.max(baseSize * 2, 25); // Make selected city much larger
            } else {
                return 0; // Hide other cities
            }
        }
        
        // If it's a map selection (click from map), show selected city larger but keep others visible
        if (selectedCountry === location) {
            return Math.max(baseSize * 1.5, 18); // Make selected city larger
        }
        
        // Normal size calculation for other cities
        const sizeMultiplier = Math.sqrt(count / maxCount);
        return Math.max(baseSize * sizeMultiplier, 5);
    };

    const getMarkerColor = (location) => {
        return selectedCountry === location ? '#ef4444' : '#3b82f6';
    };

    const getMarkerOpacity = (marker) => {
        const { location } = marker;
        
        // If it's a list selection, only show the selected city
        if (isListSelection && selectedCountry) {
            return location === selectedCountry ? 1 : 0;
        }
        
        // If there's a selection from map, dim other cities slightly
        if (selectedCountry && location !== selectedCountry) {
            return 0.5;
        }
        
        return 0.8;
    };

    const maxCount = Math.max(...markers.map(m => m.count), 1);

    return (
        <>
            {markers.map((marker, index) => {
                const radius = getMarkerRadius(marker, maxCount);
                const opacity = getMarkerOpacity(marker);
                
                // Don't render markers with 0 radius or opacity
                if (radius === 0 || opacity === 0) return null;
                
                return (
                    <CircleMarker
                        key={index}
                        center={marker.coordinates}
                        radius={radius}
                        pathOptions={{
                            color: '#ffffff',
                            weight: selectedCountry === marker.location ? 3 : 2,
                            fillColor: getMarkerColor(marker.location),
                            fillOpacity: opacity
                        }}
                        eventHandlers={{
                            click: () => onLocationClick(marker.location, 'map')
                        }}
                    >
                        {/* Tooltip shows on hover */}
                        <div style={{ display: 'none' }}>
                            {marker.location}: {marker.count} connexions
                        </div>
                    </CircleMarker>
                );
            })}
        </>
    );
}

function WorldMap({ logs, selectedCountry, onLocationClick, isListSelection = false }) {
    const [markers, setMarkers] = useState([]);
    const [mapCenter, setMapCenter] = useState([41.4, 2.2]); // Default to Catalunya

    useEffect(() => {
        // Process logs to get location statistics with real coordinates
        const locationData = {};
        const authenticatedLogs = logs.filter(log => 
            log.userId && log.userId !== 'anonymous' && log.userId !== 'null'
        );

        authenticatedLogs.forEach(log => {
            // Use city if available, otherwise use country
            const location = log.city || log.country;
            const coordinates = log.ll; // [latitude, longitude] from geoip-lite
            
            if (location && coordinates && Array.isArray(coordinates) && coordinates.length === 2) {
                if (!locationData[location]) {
                    locationData[location] = {
                        coordinates: coordinates,
                        count: 0
                    };
                }
                locationData[location].count += log.count || 1;
            }
        });

        // Create markers from the collected data
        const newMarkers = Object.entries(locationData)
            .map(([location, data]) => ({
                location,
                coordinates: data.coordinates,
                count: data.count
            }))
            .sort((a, b) => b.count - a.count);

        setMarkers(newMarkers);

        // Always center the map on the selected location (regardless of how it was selected)
        if (selectedCountry) {
            const selectedMarker = newMarkers.find(m => m.location === selectedCountry);
            if (selectedMarker) {
                setMapCenter(selectedMarker.coordinates);
            }
        } else if (newMarkers.length > 0) {
            // No location selected → center on the location with the most connections
            setMapCenter(newMarkers[0].coordinates);
        }
    }, [logs, selectedCountry, isListSelection]);

    // Auto-center map whenever a location is selected (from list OR map)
    useEffect(() => {
        if (selectedCountry) {
            const selectedMarker = markers.find(m => m.location === selectedCountry);
            if (selectedMarker) {
                setMapCenter(selectedMarker.coordinates);
            }
        }
    }, [selectedCountry, markers]);

    return (
        <div className={`world-map-container ${isListSelection && selectedCountry ? 'list-selection-mode' : ''}`}>
            <h3>🌍 Mapa mundial de connexions</h3>
            <div className="map-wrapper" style={{ height: '400px', width: '100%' }}>
                <MapContainer
                    center={mapCenter}
                    zoom={isListSelection && selectedCountry ? 10 : 9}
                    key={`${mapCenter[0]}-${mapCenter[1]}-${isListSelection}-${selectedCountry}`} // Force re-render when center changes
                    style={{ height: '100%', width: '100%' }}
                    maxZoom={18}
                    minZoom={1}
                >
                    {/* CARTO basemaps now need an API key and watermark the tiles without one */}
                    <TileLayer
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        maxZoom={19}
                    />
                    <ZoomHandler 
                        onZoomChange={() => {}} 
                        markers={markers}
                        selectedCountry={selectedCountry}
                        onLocationClick={onLocationClick}
                        isListSelection={isListSelection}
                    />
                </MapContainer>
            </div>
            <div className="map-legend">
                <div className="legend-item">
                    <div className="legend-circle small"></div>
                    <span>Poques connexions</span>
                </div>
                <div className="legend-item">
                    <div className="legend-circle large"></div>
                    <span>Moltes connexions</span>
                </div>
                <div className={`legend-note ${isListSelection && selectedCountry ? 'list-selection' : ''}`}>
                    <span>
                        {isListSelection && selectedCountry 
                            ? `Mostrant només: ${selectedCountry}` 
                            : 'Fes clic als pins per filtrar per ubicació'
                        }
                    </span>
                </div>
            </div>
        </div>
    );
}

export default WorldMap;